import { LockKeyhole, RefreshCw, Sparkles } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { Card } from './ui'
import { getRevealView, type RevealMetadata, type RevealSecret } from '../domain/reveal'

function formatCountdown(target: string, now: number): string {
  const remaining = Math.max(0, Date.parse(target) - now)
  const hours = Math.floor(remaining / 3_600_000)
  const minutes = Math.floor((remaining % 3_600_000) / 60_000)
  return `${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')}`
}

export function RevealCard({
  metadata,
  secret,
  online,
  clockOrigin,
}: {
  metadata: RevealMetadata
  secret: RevealSecret | null
  online: boolean
  clockOrigin?: { serverTime: string; receivedAtDevice: string } | null
}) {
  const [now, setNow] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [])
  const authoritativeNow = clockOrigin
    ? Date.parse(clockOrigin.serverTime) + Math.max(0, now - Date.parse(clockOrigin.receivedAtDevice))
    : now
  const view = useMemo(() => getRevealView(metadata, secret, new Date(authoritativeNow), online), [authoritativeNow, metadata, online, secret])

  if (view.state === 'revealed') {
    return (
      <Card className="reveal-card reveal-card--revealed">
        <Sparkles size={20} aria-hidden="true" />
        <p className="eyebrow">SURPRISE RÉVÉLÉE</p>
        <h2>{view.title}</h2>
        {view.detail ? <span>{view.detail}</span> : null}
      </Card>
    )
  }

  if (view.state === 'next_connection') {
    return (
      <Card className="reveal-card reveal-card--locked">
        <RefreshCw size={20} aria-hidden="true" />
        <p className="eyebrow">SURPRISE PROGRAMMÉE</p>
        <h2>Disponible à la prochaine connexion</h2>
        <span>{view.teaser}</span>
      </Card>
    )
  }

  return (
    <Card className="reveal-card reveal-card--locked">
      <LockKeyhole size={20} aria-hidden="true" />
      <p className="eyebrow">SURPRISE PROGRAMMÉE</p>
      <h2>{view.teaser}</h2>
      <span>
        {view.state === 'scheduled'
          ? Date.parse(view.scheduledFor) <= authoritativeNow && online
            ? 'Vérification sécurisée à la prochaine connexion.'
            : `Révélation dans ${formatCountdown(view.scheduledFor, authoritativeNow)}`
          : 'Brouillon administrateur'}
      </span>
    </Card>
  )
}
